/**
 * The Badge Forge choreography (design variant C): the old badge shakes,
 * a white flash pops, the badge shatters into four fragments that fly out,
 * and the new badge slams in over slowly-spinning light rays.
 * One fixed timeline per mount — the host remounts per celebration.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Platform, View, type ImageSourcePropType } from 'react-native';
import { Image } from 'expo-image';
import Svg, { Circle, Defs, Path, RadialGradient, Stop } from 'react-native-svg';
import {
  FORGE_BADGE_SIZE,
  FORGE_FRAGMENT_FLINGS,
  FORGE_TIMELINE,
} from '../../constants/celebrations.constants';
import { getRankUpCelebrationStyles } from './RankUpCelebration.styles';

const USE_NATIVE = Platform.OS !== 'web';
const DESIGN_EASE = Easing.bezier(0.16, 1, 0.3, 1);
const HALF_BADGE = FORGE_BADGE_SIZE / 2;

// Old-badge shake keyframes (translate pt / rotate deg) at ~112ms steps
const SHAKE_STEPS = [
  { x: -3, y: 2, r: -2 },
  { x: 3, y: -2, r: 2 },
  { x: -4, y: -2, r: -3 },
  { x: 4, y: 3, r: 3 },
  { x: -5, y: 2, r: -4 },
  { x: 5, y: -3, r: 4 },
  { x: -6, y: -2, r: -5 },
  { x: 0, y: 0, r: 0 },
];

function wedgePath(centerDeg: number, widthDeg: number, r: number, c: number): string {
  const a1 = ((centerDeg - widthDeg / 2) * Math.PI) / 180;
  const a2 = ((centerDeg + widthDeg / 2) * Math.PI) / 180;
  const x1 = c + r * Math.cos(a1);
  const y1 = c + r * Math.sin(a1);
  const x2 = c + r * Math.cos(a2);
  const y2 = c + r * Math.sin(a2);
  return `M${c},${c} L${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 0 1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`;
}

const RAY_WEDGES = [
  { path: wedgePath(25, 50, 160, 160), gradient: 'forgeRayGold' },
  { path: wedgePath(145, 50, 160, 160), gradient: 'forgeRayPurple' },
  { path: wedgePath(265, 50, 160, 160), gradient: 'forgeRayGold' },
];

// Quadrant crops of the old badge (top-left, top-right, bottom-left, bottom-right)
const FRAGMENT_QUADRANTS = [
  { left: 0, top: 0, imgLeft: 0, imgTop: 0 },
  { left: HALF_BADGE, top: 0, imgLeft: -HALF_BADGE, imgTop: 0 },
  { left: 0, top: HALF_BADGE, imgLeft: 0, imgTop: -HALF_BADGE },
  { left: HALF_BADGE, top: HALF_BADGE, imgLeft: -HALF_BADGE, imgTop: -HALF_BADGE },
];

export interface BadgeForgeStageProps {
  fromBadge: ImageSourcePropType;
  toBadge: ImageSourcePropType;
}

export const BadgeForgeStage: React.FC<BadgeForgeStageProps> = ({ fromBadge, toBadge }) => {
  const styles = useMemo(() => getRankUpCelebrationStyles(), []);

  const shakeX = useRef(new Animated.Value(0)).current;
  const shakeY = useRef(new Animated.Value(0)).current;
  const shakeRot = useRef(new Animated.Value(0)).current;
  const oldOp = useRef(new Animated.Value(1)).current;
  const flashOp = useRef(new Animated.Value(0)).current;
  const newOp = useRef(new Animated.Value(0)).current;
  const newScale = useRef(new Animated.Value(3.2)).current;
  const raysOp = useRef(new Animated.Value(0)).current;
  const raysScale = useRef(new Animated.Value(0.4)).current;
  const raysRot = useRef(new Animated.Value(0)).current;
  const fragments = useRef(
    FORGE_FRAGMENT_FLINGS.map(() => ({
      x: new Animated.Value(0),
      y: new Animated.Value(0),
      op: new Animated.Value(0),
      progress: new Animated.Value(0),
    }))
  ).current;

  useEffect(() => {
    const stepMs = FORGE_TIMELINE.SHAKE_MS / SHAKE_STEPS.length;
    const shakeSeq = (value: Animated.Value, key: 'x' | 'y' | 'r') =>
      Animated.sequence(
        SHAKE_STEPS.map((s) =>
          Animated.timing(value, {
            toValue: s[key],
            duration: stepMs,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: USE_NATIVE,
          })
        )
      );

    const fragmentAnims = fragments.map((f) =>
      Animated.sequence([
        Animated.delay(FORGE_TIMELINE.FRAGMENTS_DELAY_MS),
        Animated.timing(f.op, { toValue: 1, duration: 1, useNativeDriver: USE_NATIVE }),
        Animated.parallel([
          Animated.timing(f.progress, {
            toValue: 1,
            duration: FORGE_TIMELINE.FRAGMENTS_MS,
            easing: DESIGN_EASE,
            useNativeDriver: USE_NATIVE,
          }),
          Animated.timing(f.op, {
            toValue: 0,
            duration: FORGE_TIMELINE.FRAGMENTS_MS,
            useNativeDriver: USE_NATIVE,
          }),
        ]),
      ])
    );

    const timeline = Animated.parallel([
      shakeSeq(shakeX, 'x'),
      shakeSeq(shakeY, 'y'),
      shakeSeq(shakeRot, 'r'),
      Animated.sequence([
        Animated.delay(FORGE_TIMELINE.FRAGMENTS_DELAY_MS),
        Animated.timing(oldOp, { toValue: 0, duration: 1, useNativeDriver: USE_NATIVE }),
      ]),
      Animated.sequence([
        Animated.delay(FORGE_TIMELINE.FLASH_DELAY_MS),
        Animated.timing(flashOp, { toValue: 1, duration: 75, useNativeDriver: USE_NATIVE }),
        Animated.timing(flashOp, { toValue: 0, duration: 425, useNativeDriver: USE_NATIVE }),
      ]),
      ...fragmentAnims,
      Animated.sequence([
        Animated.delay(FORGE_TIMELINE.SLAM_DELAY_MS),
        Animated.parallel([
          Animated.timing(newOp, { toValue: 1, duration: 250, useNativeDriver: USE_NATIVE }),
          Animated.sequence([
            Animated.timing(newScale, { toValue: 0.9, duration: 385, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
            Animated.timing(newScale, { toValue: 1.08, duration: 133, useNativeDriver: USE_NATIVE }),
            Animated.timing(newScale, { toValue: 1, duration: 182, useNativeDriver: USE_NATIVE }),
          ]),
        ]),
      ]),
      Animated.sequence([
        Animated.delay(FORGE_TIMELINE.RAYS_DELAY_MS),
        Animated.parallel([
          Animated.timing(raysOp, { toValue: 1, duration: FORGE_TIMELINE.RAYS_IN_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
          Animated.timing(raysScale, { toValue: 1, duration: FORGE_TIMELINE.RAYS_IN_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
          Animated.timing(raysRot, { toValue: 90, duration: FORGE_TIMELINE.RAYS_IN_MS, easing: DESIGN_EASE, useNativeDriver: USE_NATIVE }),
        ]),
        Animated.loop(
          Animated.timing(raysRot, {
            toValue: 450,
            duration: FORGE_TIMELINE.RAYS_SPIN_MS,
            easing: Easing.linear,
            useNativeDriver: USE_NATIVE,
          })
        ),
      ]),
    ]);
    timeline.start();
    return () => timeline.stop();
    // Mount-only fixed timeline
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <View style={styles.stage} pointerEvents="none">
      {/* Spinning light rays behind everything */}
      <Animated.View
        style={[
          styles.raysWrap,
          {
            opacity: raysOp,
            transform: [
              { scale: raysScale },
              {
                rotate: raysRot.interpolate({
                  inputRange: [0, 360],
                  outputRange: ['0deg', '360deg'],
                }),
              },
            ],
          },
        ]}
      >
        <Svg width={320} height={320} viewBox="0 0 320 320">
          <Defs>
            <RadialGradient id="forgeRayGold" cx="50%" cy="50%" r="50%">
              <Stop offset="25%" stopColor="#FCD34D" stopOpacity={0.32} />
              <Stop offset="70%" stopColor="#FCD34D" stopOpacity={0} />
            </RadialGradient>
            <RadialGradient id="forgeRayPurple" cx="50%" cy="50%" r="50%">
              <Stop offset="25%" stopColor="#A78BFA" stopOpacity={0.32} />
              <Stop offset="70%" stopColor="#A78BFA" stopOpacity={0} />
            </RadialGradient>
          </Defs>
          {RAY_WEDGES.map((w, i) => (
            <Path key={i} d={w.path} fill={`url(#${w.gradient})`} />
          ))}
        </Svg>
      </Animated.View>

      {/* Old badge, shaking until it shatters */}
      <Animated.View
        style={[
          styles.stageCenterAbs,
          {
            opacity: oldOp,
            transform: [
              { translateX: shakeX },
              { translateY: shakeY },
              { rotate: shakeRot.interpolate({ inputRange: [-10, 10], outputRange: ['-10deg', '10deg'] }) },
            ],
          },
        ]}
      >
        <Image source={fromBadge} style={styles.badgeImg} contentFit="contain" alt="" />
      </Animated.View>

      {/* Old-badge fragments flying out */}
      <View style={styles.stageCenterAbs} pointerEvents="none">
        {fragments.map((f, i) => {
          const q = FRAGMENT_QUADRANTS[i];
          const fling = FORGE_FRAGMENT_FLINGS[i];
          return (
            <Animated.View
              key={i}
              style={[
                styles.fragmentClip,
                {
                  left: q.left,
                  top: q.top,
                  opacity: f.op,
                  transform: [
                    { translateX: f.progress.interpolate({ inputRange: [0, 1], outputRange: [0, fling.fx] }) },
                    { translateY: f.progress.interpolate({ inputRange: [0, 1], outputRange: [0, fling.fy] }) },
                    { rotate: f.progress.interpolate({ inputRange: [0, 1], outputRange: ['0deg', fling.fr] }) },
                    { scale: f.progress.interpolate({ inputRange: [0, 1], outputRange: [1, 0.6] }) },
                  ],
                },
              ]}
            >
              <Image
                source={fromBadge}
                style={[styles.badgeImg, { position: 'absolute', left: q.imgLeft, top: q.imgTop }]}
                contentFit="contain"
                alt=""
              />
            </Animated.View>
          );
        })}
      </View>

      {/* White flash at the shatter moment */}
      <Animated.View style={[styles.flashWrap, { opacity: flashOp }]}>
        <Svg width={200} height={200} viewBox="0 0 200 200">
          <Defs>
            <RadialGradient id="forgeFlash" cx="50%" cy="50%" r="50%">
              <Stop offset="0%" stopColor="#FFFFFF" stopOpacity={0.95} />
              <Stop offset="70%" stopColor="#FFFFFF" stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Circle cx={100} cy={100} r={100} fill="url(#forgeFlash)" />
        </Svg>
      </Animated.View>

      {/* New badge slamming in */}
      <Animated.View
        style={[styles.stageCenterAbs, { opacity: newOp, transform: [{ scale: newScale }] }]}
      >
        <Image source={toBadge} style={styles.badgeImg} contentFit="contain" alt="" />
      </Animated.View>
    </View>
  );
};
